import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Chip from '@mui/material/Chip'
import DialogContent from '@mui/material/DialogContent'
import FormControlLabel from '@mui/material/FormControlLabel'
import Typography from '@mui/material/Typography'
import { DataEditContextTextField } from 'Components/reusable/DataContext/fields/DataEditContextTextField'
import { DataEditContextTranslationImport } from 'Components/reusable/DataContext/TranslationImport'
import Grid from '@mui/material/Grid2'
import { teamAPI } from 'api/TeamAPI'
import ErrorOverlay from 'Components/General/ErrorOverlay'
import newWithEditModal from 'Components/reusable/HOC/newWithEditModal'
import MemberPicker from 'Components/reusable/InputFields/MemberPicker'
import { useNotificationPopup } from 'Components/reusable/Notification'
import PersonPicker from 'Components/reusable/InputFields/PersonPicker'
import { useUser } from 'hooks'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IError } from 'types/error'
import { OrganizationId } from 'types/ids'
import { ITeam } from 'types/teamInterfaces'
import { IOrganization, IPerson } from 'types/userInterfaces'
import AddIcon from '@mui/icons-material/Add'
import colors from 'constants/colors'

type newMember = {
  accountId: number
  manager: boolean
  name: string
}

/** @notExported */
interface ITeamModalProps {
  /** The team. */
  item: ITeam | null | undefined
  /** The submit indicator. */
  submitIndicator: boolean
  /** A function to reset the submit indicator. */
  resetSubmit: () => void
  /** The data. */
  data: ITeam
  /** Set the data. */
  setData: (data: ITeam) => void
  /** A function to close the modal. */
  onClose: ({ newItem }: { newItem?: ITeam }) => void
}

/**
 * TeamModal component.
 *
 * @param item - The team.
 * @param submitIndicator - The submit indicator.
 * @param resetSubmit - A function to reset the submit indicator.
 * @param data - The data.
 * @param onClose - A function to close the modal.
 * @returns The TeamModal component.
 * @notExported
 */
const TeamModal: React.FC<ITeamModalProps> = ({ item, submitIndicator, resetSubmit, data, setData, onClose }) => {
  const { t } = useTranslation()
  const { user, features } = useUser()
  const { setSuccessNotificationPopup } = useNotificationPopup()

  const [person, setPerson] = useState<IPerson>()
  const [manager, setManager] = useState<boolean>(false)
  const [members, setMembers] = useState<newMember[]>([])
  const [organization, setOrganization] = useState<IOrganization | null>(null)
  const [backendError, setBackendError] = useState<IError>()
  const [teamOrganizations, setTeamOrganizations] = useState<IOrganization[]>([])

  useEffect(() => {
    if (item) {
      const teamMembers: newMember[] = []
      for (const member of item.TeamMembers) {
        teamMembers.push({
          accountId: member.Account.id,
          manager: member.manager,
          name: `${member.Account.Person?.firstName} ${member.Account.Person?.lastName}`,
        })
      }
      setMembers(teamMembers)
      if (item.Organization) {
        setOrganization(item.Organization)
      }
    }
  }, [item, user])

  useEffect(() => {
    const controller = new AbortController()

    ;(async () => {
      if (user?.organizationId) {
        const results = await teamAPI.allowedTeamOrganizations(user.organizationId, controller)
        setTeamOrganizations(results)
      }
    })()

    return () => {
      controller.abort()
    }
  }, [item, user])

  useEffect(() => {
    const controller = new AbortController()

    ;(async () => {
      if (submitIndicator === true) {
        try {
          let newItem: ITeam
          if (item) {
            newItem = await teamAPI.save(item.id, { ...data, members }, controller)
          } else {
            newItem = await teamAPI.create({ ...data, members }, controller)
          }
          onClose({ newItem })
          setSuccessNotificationPopup()
        } catch (err) {
          setBackendError(err as IError)
          resetSubmit()
        }
      }
    })()

    return () => {
      controller.abort()
    }
  }, [submitIndicator, item, data, members, onClose, resetSubmit, setSuccessNotificationPopup])

  const addMember = () => {
    if (person) {
      const member = {
        accountId: person.accountId,
        manager: manager,
        name: `${person.firstName} ${person.lastName}`,
      }
      setMembers([...members, member])
    }
    setPerson(undefined)
    setManager(false)
  }

  const handleLimitAssignments = (e: React.ChangeEvent<HTMLInputElement>) => {
    setData({ ...data, limitAssignments: e.target.checked })
  }

  const memberChips = useMemo(() => {
    const removeMember = (member: newMember) => {
      setMembers(members.filter(m => m.accountId !== member.accountId))
    }

    const promoteManager = (member: newMember) => {
      const newMembers = [...members]

      const index = newMembers.findIndex(m => m.accountId === member.accountId)
      newMembers[index].manager = newMembers[index].manager === true ? false : true

      setMembers(newMembers)
    }

    return (
      <>
        {members.map((member, i) =>
          member.manager ? (
            <Chip
              key={i}
              sx={{ mr: 1, mb: 1, borderRadius: 1 }}
              label={
                <Typography noWrap fontSize="1em">
                  {`${member.name} `}
                  <span style={{ fontSize: '0.7em', color: colors.green }}>{t('team.modal.manager')}</span>
                </Typography>
              }
              onDelete={() => removeMember(member)}
              onClick={() => promoteManager(member)}
              variant="outlined"
            />
          ) : (
            <Chip
              key={i}
              sx={{ mr: 1, mb: 1, borderRadius: 1 }}
              label={<Typography fontSize="1em">{member.name}</Typography>}
              onDelete={() => removeMember(member)}
              onClick={() => promoteManager(member)}
              variant="outlined"
            />
          )
        )}
      </>
    )
  }, [members, t])

  if (backendError && backendError.name !== 'CanceledError' && backendError.name !== 'AbortError') {
    return <ErrorOverlay error={backendError} setOpen={setBackendError} />
  }

  return (
    <DialogContent>
      <Grid container spacing={2}>
        <Grid
          size={{
            md: 6,
            sm: 6,
            xs: 12,
          }}
        >
          <DataEditContextTranslationImport<ITeam> />
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            {t('team.info')}
          </Typography>
          <DataEditContextTextField<ITeam> field="name" required fullWidth insetLabel />
          <DataEditContextTextField<ITeam>
            field="description"
            fullWidth
            maxHintLength={1500}
            multiline
            grow
            rows={10}
            insetLabel
          />
          <FormControlLabel
            control={
              <Checkbox onChange={handleLimitAssignments} checked={data.limitAssignments} style={{ zIndex: 0 }} />
            }
            label={t(`team.${features.includes('assignments') ? 'limitAssignments' : 'limitWorkOrders'}`)}
          />
        </Grid>
        <Grid
          size={{
            md: 6,
            sm: 6,
            xs: 12,
          }}
        >
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            {t('team.members')}
          </Typography>
          {teamOrganizations && teamOrganizations.length > 0 && (
            <MemberPicker
              type="teamOrganization"
              value={organization}
              onChange={newOrg => {
                setOrganization(newOrg as IOrganization)
              }}
              teamOrganizations={teamOrganizations}
              insetLabel
              label={t('team.modal.organization')}
            />
          )}
          <PersonPicker
            value={person ?? null}
            organizationId={
              teamOrganizations && organization ? organization.id : (user?.organizationId as OrganizationId)
            }
            members={members}
            onChange={newValue => {
              if (newValue) {
                setPerson(newValue)
              }
            }}
            insetLabel
            label={t('team.modal.person')}
          />
          <Grid container my={1}>
            <Button
              data-testid="button-team-modal-add-member"
              startIcon={<AddIcon />}
              variant="contained"
              size="small"
              color="primary"
              onClick={addMember}
            >
              {t('add')}
            </Button>
          </Grid>
          {members.length > 0 && (
            <Grid>
              <Typography my={2}>{t('team.modal.managerOnClick')}</Typography>
            </Grid>
          )}
          <Grid>{memberChips}</Grid>
        </Grid>
      </Grid>
    </DialogContent>
  )
}

export default newWithEditModal(TeamModal)
